/**
 * Call State Resolution
 *
 * Pure functions for resolving CallState from various contexts.
 */

import {
  SIP_STATUS_TO_CALL_STATE,
  TERMINAL_STATES,
} from 'Modules/softphone/constants';
import type {
  CallState,
  CallDirection,
  CallStateContext,
} from 'Modules/softphone/types';
import { SessionState } from 'sip.js';

/**
 * Maps SIP.js SessionState to CallState
 * Used as fallback when no special conditions apply
 *
 * @param sessionState - SIP.js session state
 * @param direction - Call direction (inbound/outbound)
 * @returns The mapped CallState
 */
function mapSessionState(
  sessionState: SessionState,
  direction: CallDirection
): CallState {
  switch (sessionState) {
    case SessionState.Initial:
      return 'idle';

    case SessionState.Establishing:
      return direction === 'outbound' ? 'connecting' : 'ringing';

    case SessionState.Established:
      return 'active';

    case SessionState.Terminating:
    case SessionState.Terminated:
      return 'ended';

    default:
      return 'idle';
  }
}

/**
 * Resolves the CallState from a complete context
 *
 * Priority order:
 * 1. SIP status code (for error states)
 * 2. Hold state
 * 3. Transport disconnection
 * 4. Session state
 */
export function resolveCallState(context: CallStateContext): CallState {
  const {
    sessionState,
    direction,
    isOnHold,
    sipStatusCode,
    isTransportDisconnected,
  } = context;

  // 1. Check SIP status code first (error conditions take priority)
  if (sipStatusCode !== undefined) {
    const mappedState = SIP_STATUS_TO_CALL_STATE[sipStatusCode];
    if (mappedState) {
      return mappedState;
    }
  }

  // 2. Check hold state (only when session is established)
  if (isOnHold && sessionState === SessionState.Established) {
    return 'held';
  }

  // 3. Check transport disconnection (reconnecting state)
  if (isTransportDisconnected && sessionState === SessionState.Established) {
    return 'reconnecting';
  }

  // 4. Map session state
  return mapSessionState(sessionState, direction);
}

/**
 * Checks if a CallState is terminal (call has ended)
 *
 * @param state - The CallState to check
 * @returns True if the state is terminal
 */
export function isTerminalState(state: CallState): boolean {
  return TERMINAL_STATES.includes(state);
}
